import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Homework4Question.css';

const Homework5Question1: React.FC = () => {
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [filteredImage, setFilteredImage] = useState<string | null>(null);
  const [thresholdedImage, setThresholdedImage] = useState<string | null>(null);
  const [enhancedImage, setEnhancedImage] = useState<string | null>(null);
  const [filterVisualization, setFilterVisualization] = useState<string | null>(null);
  const [spectrumImage, setSpectrumImage] = useState<string | null>(null);
  
  const [D0, setD0] = useState<number>(50);
  const [order, setOrder] = useState<number>(4);
  const [threshold, setThreshold] = useState<number>(0.3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-load sample image on component mount
  useEffect(() => {
    const loadSampleImage = async () => {
      try {
        const response = await fetch('/sample-images/HW5_1.jpg');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onload = (e) => {
          setOriginalImage(e.target?.result as string);
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error('Failed to load sample image:', err);
      }
    };
    loadSampleImage();
  }, []);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImage(e.target?.result as string);
        // Clear previous results
        setFilteredImage(null);
        setThresholdedImage(null);
        setEnhancedImage(null);
        setFilterVisualization(null);
        setSpectrumImage(null);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const applyFilter = async (resultType: 'filtered' | 'thresholded' | 'enhanced') => {
    if (!originalImage) {
      setError('Please upload an image first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const base64Image = originalImage.split(',')[1];

      const response = await axios.post(
        'https://us-central1-image-processing-vk.cloudfunctions.net/process_homework5_q1',
        {
          image: base64Image,
          D0: D0,
          order: order,
          threshold: threshold,
          result_type: resultType
        },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 60000
        }
      );

      if (response.data.success) {
        const resultBase64 = `data:image/png;base64,${response.data.processed_image}`;
        
        if (resultType === 'filtered') {
          setFilteredImage(resultBase64);
        } else if (resultType === 'thresholded') {
          setThresholdedImage(resultBase64);
        } else {
          setEnhancedImage(resultBase64);
        }

        // Set filter and spectrum (same for all types)
        setFilterVisualization(`data:image/png;base64,${response.data.filter_image}`);
        setSpectrumImage(`data:image/png;base64,${response.data.spectrum_image}`);
      }
    } catch (err: any) {
      console.error('Error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to process image');
    } finally {
      setLoading(false);
    }
  };

  const applyAllSteps = async () => {
    if (!originalImage) {
      setError('Please upload an image first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const base64Image = originalImage.split(',')[1];

      // Apply all three steps in sequence
      const [filteredRes, thresholdedRes, enhancedRes] = await Promise.all([
        axios.post(
          'https://us-central1-image-processing-vk.cloudfunctions.net/process_homework5_q1',
          {
            image: base64Image,
            D0: D0,
            order: order,
            threshold: threshold,
            result_type: 'filtered'
          },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 60000
          }
        ),
        axios.post(
          'https://us-central1-image-processing-vk.cloudfunctions.net/process_homework5_q1',
          {
            image: base64Image,
            D0: D0,
            order: order,
            threshold: threshold,
            result_type: 'thresholded'
          },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 60000
          }
        ),
        axios.post(
          'https://us-central1-image-processing-vk.cloudfunctions.net/process_homework5_q1',
          {
            image: base64Image,
            D0: D0,
            order: order,
            threshold: threshold,
            result_type: 'enhanced'
          },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 60000
          }
        )
      ]);

      if (filteredRes.data.success) {
        setFilteredImage(`data:image/png;base64,${filteredRes.data.processed_image}`);
        setFilterVisualization(`data:image/png;base64,${filteredRes.data.filter_image}`);
        setSpectrumImage(`data:image/png;base64,${filteredRes.data.spectrum_image}`);
      }
      if (thresholdedRes.data.success) {
        setThresholdedImage(`data:image/png;base64,${thresholdedRes.data.processed_image}`);
      }
      if (enhancedRes.data.success) {
        setEnhancedImage(`data:image/png;base64,${enhancedRes.data.processed_image}`);
      }

    } catch (err: any) {
      console.error('Error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to process image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hw4-question-container">
      <div className="floating-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
      </div>

      <div className="content-wrapper">
        <Link to="/homework/5" className="back-button">
          ← Back to Homework 5
        </Link>

        <header className="question-header">
          <div className="badge">Question 1</div>
          <h1>Butterworth Highpass Filter</h1>
          <p className="subtitle">Edge enhancement using frequency domain filtering with thresholding</p>
        </header>

        <div className="algorithm-info">
          <h3>Butterworth Highpass Filtering</h3>
          <p>
            Highpass filters enhance edges and high-frequency details by attenuating low-frequency components.
            This implementation follows a 3-step process:
          </p>
          <ol>
            <li><strong>Filtered Image:</strong> Apply Butterworth HPF in frequency domain (FFT → Filter → IFFT)</li>
            <li><strong>Thresholded Image:</strong> Apply threshold to filtered result to create binary edge map</li>
            <li><strong>Enhanced Image:</strong> Add thresholded edges back to original image for sharpening</li>
          </ol>
          <p>
            The Butterworth filter provides a smooth transition between passed and filtered frequencies, 
            reducing ringing artifacts compared to ideal filters.
          </p>
        </div>

        <div className="controls-section">
          <div className="control-group">
            <label htmlFor="D0">Cutoff Frequency (D₀):</label>
            <input
              id="D0"
              type="range"
              min="10"
              max="200"
              value={D0}
              onChange={(e) => setD0(Number(e.target.value))}
              className="gamma-slider"
            />
            <span className="gamma-value">{D0}</span>
          </div>

          <div className="control-group">
            <label htmlFor="order">Filter Order (n):</label>
            <select
              id="order"
              value={order}
              onChange={(e) => setOrder(Number(e.target.value))}
              className="window-select"
            >
              <option value={2}>2</option>
              <option value={4}>4</option>
              <option value={6}>6</option>
              <option value={8}>8</option>
            </select>
          </div>

          <div className="control-group">
            <label htmlFor="threshold">Threshold:</label>
            <input
              id="threshold"
              type="range"
              min="0.1"
              max="1.0"
              step="0.1"
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
              className="gamma-slider"
            />
            <span className="gamma-value">{threshold.toFixed(1)}</span>
          </div>

          <div className="upload-area">
            <label htmlFor="image-upload" className="upload-label">
              <span className="upload-icon">📁</span>
              <span>Choose Image</span>
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </label>
            <button
              onClick={applyAllSteps}
              disabled={!originalImage || loading}
              className="process-button"
            >
              {loading ? 'Processing...' : 'Apply All Steps'}
            </button>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>

        <div className="images-grid-8">
          <div className="image-panel">
            <h3>Original Image</h3>
            <div className="image-container">
              {originalImage ? (
                <img src={originalImage} alt="Original" />
              ) : (
                <div className="placeholder">Upload an image</div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>Spectrum (FFT)</h3>
            <div className="image-container">
              {spectrumImage ? (
                <img src={spectrumImage} alt="Spectrum" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result will appear here'}
                </div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>Filter (HPF)</h3>
            <div className="image-container">
              {filterVisualization ? (
                <img src={filterVisualization} alt="Filter" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result will appear here'}
                </div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>Filtered Image</h3>
            <div className="image-container">
              {filteredImage ? (
                <img src={filteredImage} alt="Filtered" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result will appear here'}
                </div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>Thresholded</h3>
            <div className="image-container">
              {thresholdedImage ? (
                <img src={thresholdedImage} alt="Thresholded" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result will appear here'}
                </div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>Enhanced</h3>
            <div className="image-container">
              {enhancedImage ? (
                <img src={enhancedImage} alt="Enhanced" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result will appear here'}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="info-box">
          <h4>Parameter Guide</h4>
          <p>
            <strong>Cutoff Frequency (D₀):</strong> Controls the radius of the highpass filter. Lower values preserve more low frequencies (less edge enhancement), higher values remove more low frequencies (more edge enhancement).<br/><br/>
            <strong>Filter Order (n):</strong> Controls the steepness of the filter transition. Higher orders provide sharper cutoffs but may introduce ringing.<br/><br/>
            <strong>Threshold:</strong> Used to create binary edge map from filtered image. Lower values include weaker edges, higher values only strong edges.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Homework5Question1;

